import { campaignTagsSchema, collectCampaignTags } from './analytics/campaign-tags.js';

import type { RedirectLoginOptions } from '@auth0/auth0-spa-js';
import type { CampaignTags } from './analytics/campaign-tags.js';
import type { Auth0AppState } from './auth0-app-state.js';
import type { Auth0ScreenHint } from './auth0-screen-hint.js';

/** The ID token claim carrying the campaign an Auth0 Action saved on the user when the account was created. */
export const AUTH0_CAMPAIGN_CLAIM = 'https://cadstrom.io/campaign';

/** What Auth0 accepts as an `ext-` param value. A value outside it makes `/authorize` fail before any Action runs. */
export const AUTH0_EXT_VALUE_PATTERN = /^[-\w.*~@+ /:]{1,255}$/;

const EXT_PARAM_PREFIX = 'ext-';

/**
 * Names each tag as an `ext-<tag>` param for `/authorize`, which is how it reaches the Auth0 Action that saves the
 * campaign on the user. A tag whose value Auth0 would reject is left out, since sending it would block the login.
 *
 * @param tags - The campaign tags of the login.
 * @returns The `ext-` params to add to the authorization request.
 */
export function toAuth0ExtParams(tags: CampaignTags): Record<string, string> {
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(tags)) {
		if (typeof value === 'string' && AUTH0_EXT_VALUE_PATTERN.test(value)) {
			params[`${EXT_PARAM_PREFIX}${key}`] = value;
		}
	}

	return params;
}

/**
 * Reads the campaign saved on the user from the ID token claims.
 *
 * @param claims - The ID token claims, if the user is logged in.
 * @returns The saved tags; empty when the claim is absent or malformed.
 */
export function readCampaignClaim(claims: Record<string, unknown> | undefined): CampaignTags {
	const parsed = campaignTagsSchema.safeParse(claims?.[AUTH0_CAMPAIGN_CLAIM]);
	return parsed.success ? parsed.data : {};
}

/**
 * Chooses the campaign to attribute a signup to. The saved claim wins because it was captured when the account was
 * created; the tags carried through the login are the fallback when no claim exists.
 *
 * @param claimTags - Tags read from the ID token claim.
 * @param appStateTags - Tags carried through the login in `appState`, if any.
 * @returns The tags to attribute the signup to; empty when neither source had any.
 */
export function resolveSignupCampaign(claimTags: CampaignTags, appStateTags: CampaignTags | undefined): CampaignTags {
	return Object.keys(claimTags).length > 0 ? claimTags : (appStateTags ?? {});
}

/** What a login redirect needs to know. */
export interface LoginRedirectInput {
	/** The path to land on after logging in. */
	returnTo: string;
	/** The campaign tags of the visit that began the login. */
	tags: CampaignTags;
	/** Opens the signup tab instead of the login tab. */
	screenHint?: Auth0ScreenHint | undefined;
}

/**
 * Builds the options for `loginWithRedirect`, so every login entry point carries the campaign the same way.
 *
 * @param input - The path to return to, the campaign tags and an optional screen hint.
 * @returns Options that carry the tags both in `appState` and as `ext-` params.
 */
export function buildLoginRedirectOptions(input: LoginRedirectInput): RedirectLoginOptions<Auth0AppState> {
	return {
		appState: { returnTo: input.returnTo, campaign: input.tags },
		authorizationParams: {
			screen_hint: input.screenHint,
			...toAuth0ExtParams(input.tags),
		},
	};
}

/**
 * Collects the campaign tags of the page that is starting a login, from its link and referrer.
 *
 * @param search - The page's query params; defaults to those of the current URL.
 * @returns The tags, excluding referrals from this app or the login provider.
 */
export function collectLoginCampaignTags(
	search: Record<string, unknown> = Object.fromEntries(new URLSearchParams(window.location.search)),
): CampaignTags {
	return collectCampaignTags({
		search,
		referrer: document.referrer,
		ownHostnames: [window.location.hostname, import.meta.env.VITE_AUTH0_DOMAIN],
	});
}
